'use client'

import { cn } from '@/lib/utils'
import { ArrowLeftCircle, ArrowRightCircle } from 'lucide-react'
import { Button } from '../ui/button'

/**
 * Get an array from x to x + 5 (or max) for page buttons
 * @param {number} currentPage
 * @param {number} pageLimit
 * @returns {Array<number>} Array of page numbers
 * @example
 *   getPageNumbers(1, 5) // [1, 2, 3, 4, 5]
 *   getPageNumbers(2, 5) // [1, 2, 3, 4, 5]
 *   getPageNumbers(5, 8) // [3, 4, 5, 6, 7]
 */
export function getPageNumbers(currentPage, pageLimit) {
  const maxButtons = 5

  // Ensure the start page is within the correct range
  let startPage = Math.max(1, currentPage - Math.floor(maxButtons / 2))
  let endPage = startPage + maxButtons - 1

  // Ensure the range does not exceed the pageLimit boundaries
  if (endPage > pageLimit) {
    endPage = pageLimit
    startPage = Math.max(1, endPage - maxButtons + 1)
  }

  return Array.from(
    { length: endPage - startPage + 1 },
    (_, i) => startPage + i,
  )
}

/**
 *
 * @param {number} page - The current page number (starts at 1)
 * @param {number} pageLimit - The maximum number of pages to show
 * @param {function} setPage - A function to set the current page number
 * @param {string} className - The className to apply to the pager container
 * @returns
 */
export default function Pager({
  page = 1,
  pageLimit = 5,
  setPage = () => console.error('No `setPage` function set for Pager!'),
  className = '',
}) {
  return (
    <div className={cn('flex justify-between desktop-xs:gap-3', className)}>
      {/* Back Button */}
      <Button
        disabled={page === 1}
        onClick={() => setPage(page - 1)}
        className="bg-neutral-blue-700! group grid size-10 items-center justify-center rounded p-0 disabled:opacity-100 desktop-xs:size-12"
      >
        <ArrowLeftCircle className="size-6! stroke-white group-disabled:stroke-[#5f656c]" />
      </Button>

      {/* Page Buttons */}
      <div className="flex gap-2 desktop-xs:gap-3">
        {getPageNumbers(page, pageLimit).map((pageNum, i) => (
          <Button
            key={`page-selector-${pageNum}`}
            onClick={() => setPage(pageNum)}
            className={cn(
              'bg-neutral-blue-700! text-white! group grid size-10 items-center justify-center rounded p-0 text-p-1 font-bold desktop-xs:size-12',
              {
                'bg-shades-0! text-black!': page === pageNum,
              },
            )}
          >
            {pageNum}
          </Button>
        ))}
      </div>

      {/* Forward Button */}
      <Button
        disabled={page === pageLimit}
        onClick={() => setPage(page + 1)}
        className="bg-neutral-blue-700! group grid size-10 items-center justify-center rounded p-0 disabled:opacity-100 desktop-xs:size-12"
      >
        <ArrowRightCircle className="size-6! stroke-white group-disabled:stroke-[#5f656c]" />
      </Button>
    </div>
  )
}
